<template>
    <settings-wrapper title="Organization settings" section="Tokens">
        <v-skeleton-loader v-if="fetchingOrgTokens" class="mx-auto" type="table"></v-skeleton-loader>
        <template v-else>
            <div class="d-flex align-center">
                <CreateAndEditTokenDialog :disabled="fetchingOrgTokens" v-if="isOrgManager"></CreateAndEditTokenDialog>
                <v-spacer></v-spacer>
                <v-text-field
                    placeholder="Search for a token..."
                    autofocus
                    v-model="search"
                    dense
                    hide-details
                    prepend-inner-icon="mdi-magnify "
                    solo
                    flat
                    clearable
                    class="mr-2"></v-text-field>
                <v-btn @click="$store.dispatch('orgStore/fetchOrgTokens', $route.params.oid)" icon>
                    <v-icon>refresh</v-icon>
                </v-btn>
            </div>

            <v-data-table
                :headers="headers"
                :items="orgTokenData"
                item-key="otknid"
                :search="search"
                :options="tableOptions"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                class="mt-4">
                <template v-slot:top="{ pagination, options, updateOptions }">
                    <v-data-footer
                        :pagination="pagination"
                        :options="options"
                        @update:options="updateOptions"
                        :itemsPerPageOptions="itemsPerPageOptions"
                        items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                        class="no-border" />
                </template>
                <template v-slot:no-data>
                    <span>Currently you don't have any token in this organization. Click create new token to create a new one.</span>
                </template>
                <template v-slot:[`item.type`]="{ item }">
                    <div class="d-flex align-center">
                        {{ getTokenType(item.token_type) }}
                    </div>
                </template>
                <template v-slot:[`item.scope`]="{ item }">{{ getTokenScope(item.scope) }}</template>
                <template v-slot:[`item.token`]="{ item }">
                    <template v-if="item.visible">
                        <div v-if="isJson(item.token)">
                            <ul class="py-3 px-0" style="list-style-type: none">
                                <li v-for="(key, index) in Object.keys(JSON.parse(item.token))" :key="index">
                                    <div v-if="item.visible" class="d-flex align-center">
                                        <span class="caption font-weight-bold text-uppercase secondary--text mr-2">{{ key }}:</span>
                                        <span class="subtitle-2 secondary--text">{{ JSON.parse(item.token)[key] }}</span>
                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on }">
                                                <div v-on="on">
                                                    <copy-to-clipboard
                                                        :textToCopy="JSON.parse(item.token)[key]"
                                                        buttonClass="caption font-weight-bold"
                                                        buttonColor="secondary"
                                                        buttonName="copy to clipboard"
                                                        :iconButton="true"
                                                        :isSmall="true"></copy-to-clipboard>
                                                </div>
                                            </template>
                                            <span>Copy to clipboard</span>
                                        </v-tooltip>
                                    </div>
                                </li>
                            </ul>
                        </div>
                        <span v-else class="subtitle-2 secondary--text">{{ item.token }}</span>
                    </template>
                    <span v-else>&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;</span>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <v-menu left offset-x nudge-top="10">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" text color="secondary" small>
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <v-list-item @click="item.visible = !item.visible">
                                <v-list-item-icon>
                                    <v-icon color="secondary">{{ item.visible ? 'visibility' : 'visibility_off' }}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title class="secondary--text mr-5">{{ item.visible ? 'Hide token' : 'Show token' }}</v-list-item-title>
                            </v-list-item>
                            <CreateAndEditTokenDialog v-if="isOrgManager" dialogAction="editToken" activatorType="listItem" :tokenData="item" />
                            <v-divider v-if="isOrgManager" class="my-2"></v-divider>
                            <SSHDeleteDialog
                                v-if="isOrgManager"
                                activatorType="listItem"
                                buttonName="Delete token"
                                :token="item.token"
                                dialogTitle="Token Delete"
                                :apiURL="`/org_tokens/${item.otknid}`"
                                :fetchTokensDispatch="
                                    () => {
                                        $store.dispatch('orgStore/fetchOrgTokens', $route.params.oid)
                                    }
                                " />
                        </v-list>
                    </v-menu>
                </template>
            </v-data-table>
        </template>
    </settings-wrapper>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { isJson, getObjectValueByKey } from '@/utils'
import { enumsData } from '@/mixins/enums'
import SettingsWrapper from '@/components/SettingsWrapper.vue'
const SSHDeleteDialog = () => import('@/components/DeleteTokenDialog.vue')
const CopyToClipboard = () => import('@/components/CopyToClipboard')
const CreateAndEditTokenDialog = () => import('../components/TheOrgCreateAndEditTokenDialog.vue')
export default {
    name: 'OrgTokens',
    mixins: [enumsData],
    data() {
        return {
            expand: false,
            headers: [
                { text: 'Type', align: 'left', value: 'type', width: '10%' },
                { text: 'Scope', align: 'left', value: 'scope', width: '10%' },
                { text: 'Token', align: 'start', value: 'token', width: '70%' },
                { text: 'Actions', align: 'right', value: 'actions', width: '10%', sortable: false }
            ],
            itemsPerPageOptions: [25, 50, 100, -1],
            tableOptions: {
                page: 0,
                itemsPerPage: 25,
                pageStart: 1,
                pageStop: 25,
                itemsLength: 25
            },
            search: '',
            tokenVisibilityModels: null,
            orgTokenData: []
        }
    },
    components: { SSHDeleteDialog, CopyToClipboard, CreateAndEditTokenDialog, SettingsWrapper },
    methods: {
        isJson: function (str) {
            return isJson(str)
        },
        getTokenScope: function (tokenScopeCode) {
            return getObjectValueByKey(this.orgTokenScopes, tokenScopeCode)
        },
        getTokenType: function (tokenTypeCode) {
            return getObjectValueByKey(this.orgTokenTypes, tokenTypeCode)
        },
        setOrgTokens: function () {
            if (this.orgTokens && this.orgTokens.length) {
                this.orgTokenData = this.orgTokens.map(function (token) {
                    const tokenData = Object.assign({}, token)
                    tokenData.visible = false
                    return tokenData
                })
            }
        }
    },
    computed: {
        ...mapState('orgStore', ['orgTokens', 'fetchingOrgTokens']),
        ...mapGetters('orgStore', ['isOrgManager'])
    },
    mounted() {
        this.setOrgTokens()
    },
    watch: {
        orgTokens: function (nextVal) {
            if (nextVal) {
                this.setOrgTokens()
            }
        }
    }
}
</script>
